import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import LocalVideo from './LocalVideo/LocalVideo.jsx';
import './MediaDisplay.css';
import { RemoteParticipant } from "livekit-client";
import RemoteParticipantPlayer from "./RemoteParticipant/RemoteParticipantPlayer.jsx";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { setViewMode } from '../../store/livekitSlice/livekitSlice.js';
import UserAvatar from "../../../../components/UserAvatar/UserAvatar.jsx";
import HandRankBadge from "./HandRankBadge/HandRankBadge.jsx";
import Whiteboard from "../../../Whiteboard/components/Whiteboard/Whiteboard.jsx";
import { triggerHaloAnimation } from "../../../../utils/haloUtils.js";
import { useHaloSocket } from "../../../../hooks/useHaloSocket.js";
import GameManager from "../../../Games/components/GameManager.jsx";
import { setIsConferencePanelOpen } from "../../../Conference/store/ActiveConferenceSlice.js";


/**
 * Displays local and remote video tracks in a centered, vertical list.
 * @param {RemoteParticipant[]} remoteParticipants - The list of remote participants in the room.
 * @param {MediaStreamTrack} localCameraTrack - The user's own processed camera track.
 * @param {MediaStreamTrack} localScreenShareTrack - The user's own screen share track.
 */
export default function MediaDisplay({
  localCameraTrack,
  localScreenShareTrack,
  remoteParticipants,
  onRemoteScreenShare,
  room
}) {
  const [zoomedTrackSid, setZoomedTrackSid] = useState(null);
  const [videoToggleView, setVideoToggleView] = useState(false);
  const { whiteboardActive } = useSelector((state) => state.whiteboard);
  const { zoomedTrackId, videoCount, conferenceMode } = useSelector(state => state.livekit);
  const { localAvatar } = useSelector((state) => state.auth);
  const { livekitMicActive } = useSelector((state) => state.livekit);
  const { activeGames } = useSelector((state) => state.games);
  const { t } = useTranslation();
  const hands = useSelector((state) => state.raisedHands.list);
  const dispatch = useDispatch();
  const handRanks = useMemo(() => {
    const ordered = [...hands].sort((a, b) => a.time - b.time);
    const ranks = {};

    ordered.forEach((h, index) => {
      const rank = index + 1;

      if (h.sender) ranks[h.sender] = rank;

      if (h.senderEmail) ranks[h.senderEmail] = rank;
    });

    return ranks;
  }, [hands]);

  const localAvatarRef = useRef(null);
  const handleHalo = useCallback((level) => {
    triggerHaloAnimation(localAvatarRef.current, localAvatar?.id, level, 'flex');
  }, [localAvatar?.id]);

  useHaloSocket(localAvatar?.id, handleHalo);




  const participantComponents = useMemo(() => {
    if (!remoteParticipants || remoteParticipants.size === 0) return [];

    return Array.from(remoteParticipants)
    .filter(([identity]) => !identity.startsWith("agent-"))
    .map(([email, remoteParticipant]) => {
      let meta = {};
      try {
        meta = JSON.parse(remoteParticipant.participantInfo?.metadata || "{}");
      } catch { }
     const isTavusAgent = meta.hasOwnProperty('tavus') || remoteParticipant.identity.includes('tavus');
     const isLemonslice = meta.hasOwnProperty('lemonslice') || remoteParticipant.identity.includes('lemonslice');
        let displayName = null;
       if(isTavusAgent || isLemonslice)
        {
         displayName = "Anna" 
        }

      const userId = meta.id;
      const userEmail = remoteParticipant.identity;

      const rank =
        handRanks[userId] ??
        handRanks[userEmail] ??
        null;

      return (
        <RemoteParticipantPlayer
          key={email}
          remoteParticipant={remoteParticipant}
          handRank={rank}
          onRemoteScreenShare={onRemoteScreenShare}
          room={room}
          forcedName={displayName}
        />
      );
    });
  }, [remoteParticipants, handRanks]);

  const handleSetViewMode = (value) => {
    dispatch(setViewMode(value));
  };

  function useMobile() {
    const [isMobile, setIsMobile] = useState(window.innerWidth < 480);

    useEffect(() => {
      const handleResize = () => {
        setIsMobile(window.innerWidth < 700);
      };

      window.addEventListener('resize', handleResize);

      // Nettoyage
      return () => window.removeEventListener('resize', handleResize);
    }, []);

    return isMobile;
  }
  const isMobile = useMobile();

  useEffect(() => {
    if(!zoomedTrackId && conferenceMode)
    {
      dispatch(setIsConferencePanelOpen(false));
    }
  },[zoomedTrackId]);
  return (
    <>
      <div className={`toggle-view-state ${videoToggleView ? 'show' : ''}`}
        onMouseEnter={!isMobile ? () => setVideoToggleView(prev => !prev) : undefined}
        onMouseLeave={!isMobile ? () => setVideoToggleView(prev => !prev) : undefined}>
        <div className="toggle-view-container">
          <div className={`mode-map ${!conferenceMode ? 'active' : ''}`} onClick={() => handleSetViewMode(false)}>
            <span class="material-symbols-outlined">
              map
            </span>
          </div>

          <div className={`mode-map ${conferenceMode ? 'active' : ''}`} onClick={() => handleSetViewMode(true)}>
            <span class="material-symbols-outlined">
              grid_view
            </span>
          </div>
        </div>
        <div className={`toggle-view-btn ${videoToggleView ? 'open' : 'colsed'}`}>
          <span className={`material-symbols-outlined`} onClick={isMobile ? () => setVideoToggleView(prev => !prev) : undefined}>
            keyboard_arrow_down
          </span>
        </div>
      </div>

      <div className={`video-display-container ${conferenceMode ? 'conference-mode' : 'video-map-mode'} ${zoomedTrackId ? 'zoomed' : ''} d-block`} >
        <div className={`video-list-scroll ${(participantComponents.length === 0 && !localCameraTrack && !localScreenShareTrack && !whiteboardActive) ? 'only-in-room' : ''}`}>
          {(!localCameraTrack && (participantComponents.length > 0 || conferenceMode)) &&
            <div className={`user-badge ${!localCameraTrack ? 'camera-off' : ''}`}>
              <div
                ref={localAvatarRef}
                style={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
              >
                <div style={{ zIndex: 1, display: 'flex' }}>
                  <UserAvatar user={localAvatar} size={120} />
                </div>
              </div>
              <HandRankBadge rank={handRanks[localAvatar?.id]} />
              <span className={`mic-status ${livekitMicActive ? 'mic-active' : 'mic-inactive'}`}>
                {livekitMicActive ?
                  <span className="material-symbols-outlined">
                    mic
                  </span>
                  :
                  <span className="material-symbols-outlined">
                    mic_off
                  </span>
                }
              </span>

              <span className='participent-camera-status'>
                <span class="material-symbols-outlined">
                  videocam_off
                </span>
              </span>
              <span className="participant-name text-gray-800 font-medium">{t("me")}</span>
            </div>
          }
          {(localCameraTrack && zoomedTrackSid !== 'local-camera') && (
            <LocalVideo track={localCameraTrack} type="localCamera" label={t("me")} handRank={handRanks[localAvatar?.id]} conferenceMode={conferenceMode} onZoomClick={() => setZoomedTrackSid("local-camera")} />
          )}

          {(localScreenShareTrack && zoomedTrackSid !== 'local-screen') && (
            <LocalVideo track={localScreenShareTrack} type="localScreenShare" label="Partage" conferenceMode={conferenceMode} onZoomClick={() => setZoomedTrackSid("local-screen")} />
          )}
          {activeGames && activeGames.map((game) => (
            <GameManager
              key={game.gameId}
              gameData={game}
              room={room}
            />
          ))}
          {whiteboardActive && (
            <Whiteboard
              localParticipant={room.localParticipant}
              remoteParticipants={remoteParticipants}
            />
          )}
          {participantComponents}
        </div>
      </div>
    </>
  );
}